import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@apollo/client";

import { FormGroup, Input } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import { FormDialog } from "components/dialogs/Dialogs";

import { adminLanguageEntrySchema, AdminLanguageEntrySchemaInput, AdminLanguageEntrySchemaOutput } from "view/admin/utils/admin.schema";
import { AdminLanguage, CustomerTranslations, CustomTranslationEntry } from "view/admin/utils/admin.types";

import { UpdateCustomerLanguageSetDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

interface LanguageEntryTranslationEditDialogProps {
	open: boolean;
	onClose: () => void;
	languageEntry: CustomTranslationEntry;
	languages: AdminLanguage[];
	customerTranslations: CustomerTranslations;
	refetch: () => void;
}

const LanguageEntryEditDialog = ({
	open,
	onClose,
	languageEntry,
	languages,
	customerTranslations,
	refetch,
}: LanguageEntryTranslationEditDialogProps) => {

	const defaultValues = {};

	const { control, handleSubmit, reset } = useForm<AdminLanguageEntrySchemaInput, unknown, AdminLanguageEntrySchemaOutput>({
		resolver: zodResolver(adminLanguageEntrySchema),
		defaultValues: defaultValues,
	});

	const [ updateCustomerLanguageSet ] = useMutation(UpdateCustomerLanguageSetDocument);

	const save = (data: AdminLanguageEntrySchemaOutput) => {

		const translations = customerTranslations?.entries || {};

		const newTranslations = {
			...(translations[languageEntry.key] || {}),
			...data,
		};

		const mergedTranslations = {
			...translations,
			[languageEntry.key]: newTranslations,
		};

		const inputs = Object.entries(mergedTranslations).map(([ key, values ]) => ({
			key: key,
			translations: Object.entries(values).map(([ k, v ]) => ({
				key: k,
				translation: v ?? "",
			})),
			web: true,
		}));

		updateCustomerLanguageSet({
			variables: {
				inputs,
			},
		}).then(() => {
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open && languageEntry) {
			reset(languageEntry.translations);
		}
	}, [ open, languageEntry ]);

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			title={languageEntry.key}
			form='languageKeyForm'
			maxWidth="md">
			<form onSubmit={handleSubmit((form) => save(form))} id='languageKeyForm'>
				{languages.map((item, key) => (
					<Grid size={{ xs: 12 }} key={key}>
						<FormGroup label={item.description} labelPlacement='left-center' required>
							<Controller
								name={item.code.toLowerCase()}
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Grid>
				))}
			</form>
		</FormDialog>
	);
};

export default LanguageEntryEditDialog;
